import { useEffect, useRef, useState } from 'react';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Toolbar } from 'primereact/toolbar';
import { Panel } from 'primereact/panel';
import { Button } from 'primereact/button';
import { TabView, TabPanel } from 'primereact/tabview';
import { Dialog } from 'primereact/dialog';
import { Card } from 'primereact/card';
import { Column } from 'primereact/column';
import { Menu } from 'primereact/menu';
import * as generic from '../../../components/Generico';
import { useLazyParams } from '../../../hooks/useLazyParams';
import { TablaDinamica } from '../../../components/TablaDinamica';
import { AnexosComponent } from '../../../components/AnexosComponent';
import { useToken } from '../../../components/useToken';
import { FiltroMultiempresa } from '../../../components/FiltroMultiempresa';
import { DialogInstrucciones } from '../../../components/ui/DialogInstrucciones';
import { JuridicaService } from '../../../service/JuridicaService';
import { Juridica } from '../../../models/ficha/Juridica';
import { DatosGenerales } from './DatosGenerales';
import { InformacionEconomica } from './InformacionEconomica';
import { AccionistasPep } from './AccionistasPep';
import { Declaracion } from './Declaracion';

export const PersonaJuridica = () => {
    const [catalogos, setCatalogos] = useState(null);
    const omodel = new Juridica({ formularioMatriz: catalogos?.formularioMatriz });
    const toast = useRef(null);
    const juridicaSrv = new JuridicaService();
    const { getPerss, getRol } = useToken();
    // Estados globales del formulario
    const [juridica, setJuridica] = useState(null);
    const [permss, setPermss] = useState(null);
    // Estados de navegación y UI
    const [dialogJuridica, setDialogJuridica] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const [siCliente, setSiCliente] = useState(false);
    const [urls, setUrls] = useState(null);
    const [dialogSubir, setDialogSubir] = useState(false); // Anexos
    const [dialogInstruccion, setDialogInstruccion] = useState(false);
    const [soloLectura, setSoloLectura] = useState(false);
    const [empresa, setEmpresa] = useState(null);
    // Custom Hooks y Referencias
    const tablaEstado = useLazyParams({ modelo: omodel.model });
    const menuRef = useRef(null);
    const topRef = useRef(null);
    const [mostrarRegresarFlotante, setMostrarRegresarFlotante] = useState(false);

    useEffect(() => {
        setUrls({
            urlListado: 'ficha/juridica',
            urlDownload: 'ficha/juridica/descarga',
            urlUpload: 'ficha/juridica/store',
            udelete: 'ficha/juridica/eliminar-anexo',
            uactual: 'ficha/juridica/editar',
            uenviar: 'ficha/juridica/enviar',
            uaprobar: 'ficha/juridica/aprobar',
        });

        const permisosToken = getPerss();
        const esClienteNotificado = permisosToken !== undefined;
        setSiCliente(esClienteNotificado);

        if (esClienteNotificado) { // FLUJO CLIENTE NOTIFICADO
            setPermss(permisosToken);
            editFicha(true);
        }
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (getPerss() === undefined) {
            loadLazyData();
        }
        // eslint-disable-next-line
    }, [tablaEstado.lazyParams]);

    // Efecto para observar el inicio del formulario y mostrar el botón regresar flotante
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                setMostrarRegresarFlotante(!entry.isIntersecting);
            },
            { threshold: 0 }
        );
        const currentRef = topRef.current;
        if (currentRef) {
            observer.observe(currentRef);
        }
        return () => {
            if (currentRef) {
                observer.unobserve(currentRef);
            }
        };
    }, [dialogJuridica]);

    const loadLazyData = () => {
        tablaEstado.setLoading(true);
        const _lazyParams = { ...tablaEstado.lazyParams };
        delete _lazyParams.rtipo;
        delete _lazyParams.empresa;
        if (permss == null) {
            _lazyParams.rtipo = true;
        }
        if (empresa) {
            _lazyParams.empresa = empresa;
        }
        const rol = getRol();
        if (!rol) {
            toast.current.show({ severity: 'warn', summary: 'Rol no seleccionado', detail: 'Por favor selecciona un rol antes de continuar.', life: 3000 });
            tablaEstado.setLoading(false);
            return;
        } else {
            _lazyParams.rol = rol;
        }
        juridicaSrv.getJuridica({ lazyEvent: JSON.stringify(_lazyParams) }).then(res => {
            tablaEstado.setTotalRecords(res.data.total);
            tablaEstado.setData(res.data.registros);
            if (res.data.perss) {
                setPermss(res.data.perss);
            }
            if (res.data.catalogos) {
                setCatalogos(res.data.catalogos);
            }
        }).finally(() => {
            tablaEstado.setLoading(false);
        });
    }

    const iniciarNuevo = () => {
        setSoloLectura(false);
        setJuridica(null);
        setActiveIndex(0);
        setDialogJuridica(true);
        setDialogInstruccion(true);
    }

    const editFicha = (esNotificado = false, isReadOnly = false) => {
        setSoloLectura(isReadOnly);
        const idConsulta = esNotificado ? 0 : juridica?.id;
        const necesitaCatalogos = catalogos === null;
        juridicaSrv.consultarJuridica(idConsulta, null, { necesitaCatalogos: necesitaCatalogos }).then(res => {
            if (necesitaCatalogos && res.data.catalogos) {
                setCatalogos(res.data.catalogos);
            }
            if (!res.data.juridica) {
                iniciarNuevo();
                return;
            }
            setDialogJuridica(true);
            setActiveIndex(0);
            setDialogInstruccion(true);

            nuevaJuridica({ id: res.data.juridica.id, nemonico: res.data.juridica.nemonico });
        });
    }

    const confirmDelete = () => {
        confirmDialog({
            message: '¿Está seguro de eliminar el formulario?',
            header: 'Confirmación',
            icon: 'pi pi-exclamation-triangle',
            accept: () => deleteJuridica()
        });
    }

    const deleteJuridica = () => {
        juridicaSrv.eliminarJuridica(juridica?.id).then(res => {
            loadLazyData();
            generic.funcSuccess(res, toast);
        }).catch(error => {
            generic.funcDeleteError(error, toast);
        });
    }

    // FUNCIÓN VITAL PARA MANTENER SINCRONIZADAS LAS SECCIONES
    const nuevaJuridica = (nuevo) => {
        if (!nuevo) {
            setJuridica(null);
            return;
        }
        setJuridica(prev => ({ ...prev, ...nuevo }));
    }

    // CONTROLES DE NAVEGACIÓN (Siguiente / Anterior)
    const handleNext = () => {
        if (activeIndex < 3) setActiveIndex(activeIndex + 1);
    };

    const handlePrev = () => {
        if (activeIndex > 0) setActiveIndex(activeIndex - 1);
    };

    const regresar = () => {
        setActiveIndex(0);
        setDialogJuridica(false);
        setSoloLectura(false);
        if (!siCliente) loadLazyData();
    }

    const reiniciar = () => {
        if (!siCliente) loadLazyData();
        setDialogJuridica(false);
        setDialogSubir(false);
    }

    const itemsMenu = () => {
        const items = [];
        if (permss?.consultar) {
            items.push({ label: 'Ver', icon: 'pi pi-eye', command: () => { editFicha(false, true); } });
        }
        if (permss?.actualizar && (juridica?.nemonico === 'BORRADOR' || juridica?.nemonico === 'ENVIADO')) {
            items.push({ label: 'Editar', icon: 'pi pi-pencil', command: () => { editFicha(); } });
        }
        if (permss?.consultar) {
            items.push({ label: 'Anexos formulario', icon: 'pi pi-folder-open', command: () => { setDialogSubir(true); } });
        }
        if (permss?.eliminar && juridica?.nemonico === 'BORRADOR') {
            items.push({ label: 'Eliminar', icon: 'pi pi-trash', command: () => { confirmDelete(); } });
        }
        return [{ label: 'Opciones', items: items }];
    }

    const accionesTemplate = (rowData) => {
        return (
            <Button icon="pi pi-caret-down" type="button" className="p-button-rounded p-button-success mr-2" title="Opciones/Acciones" aria-controls="popup_menu" aria-haspopup
                onClick={(event) => { setJuridica(rowData); menuRef.current.toggle(event); }} />
        );
    };

    const ColumnaAcciones = (
        <Column header="Acciones" body={accionesTemplate} style={{ width: '6rem', textAlign: 'center' }} />
    );

    const panelHeaderTemplate = () => (
        <div className="p-panel-header flex justify-content-between align-items-center flex-wrap gap-2 w-full">
            <div>
                <h3 className="m-0 text-xl font-bold p-text-secondary flex align-items-center">
                    <i className="pi pi-building mr-2"></i>
                    <span className="mr-2">{soloLectura ? 'VER' : (juridica?.id ? 'EDITAR' : 'NUEVO')} FORMULARIO DE INFORMACIÓN DE PERSONA JURÍDICA</span>
                    <Button type="button" icon="pi pi-question-circle" className="p-button-rounded p-button-text p-button-sm p-0 m-0 text-white hover:text-blue-200 transition-colors transition-duration-200"
                        style={{ width: '2rem', height: '2rem' }} onClick={() => setDialogInstruccion(true)} tooltip="Ver instrucciones" tooltipOptions={{ position: 'right' }} />
                </h3>
            </div>
            {!siCliente && (
                <Button tooltip="Regresar al listado" tooltipOptions={{ position: 'left' }} icon="pi pi-arrow-left" onClick={regresar} />
            )}
        </div>
    );

    return (
        <>
            <ConfirmDialog />
            <Toast ref={toast} />
            <Menu model={itemsMenu()} popup ref={menuRef} id="popup_menu" />
            {!dialogJuridica && !siCliente && (
                <div className="fadein animation-duration-300">
                    {getRol() && getRol()?.ideg && <FiltroMultiempresa loadLazyData={loadLazyData} setEmpresa={setEmpresa} />}
                    <Toolbar className="mb-4 shadow-1 border-round" start={(getRol() && getRol()?.ideg) ? null : generic.leftToolbarTemplate(iniciarNuevo, 'Nuevo', permss)}
                        end={generic.rightToolbarTemplate(tablaEstado.data, omodel, 'Listado_persona_juridica', 'LISTADO PERSONA JURIDICA')} />

                    <Card style={{ maxWidth: '100%', margin: 'auto' }}>
                        <TablaDinamica modelo={omodel.model} estadoTabla={tablaEstado} datos={tablaEstado.data} totalRegistros={tablaEstado.totalRecords} dataKey="id" acciones={ColumnaAcciones} />
                    </Card>
                </div>
            )}
            {dialogJuridica &&
                <div className="fadein animation-duration-300">
                    <Panel headerTemplate={panelHeaderTemplate} className="panel-ficha shadow-2">
                        <div ref={topRef} style={{ position: 'absolute', top: '0', left: '0', width: '1px', height: '80px', pointerEvents: 'none', visibility: 'hidden' }} />
                        <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)} className="form-tabview">

                            <TabPanel header="Sección 1" rightIcon="pi pi-id-card ml-2">
                                <DatosGenerales toast={toast} catalogos={catalogos} next={handleNext} id={juridica?.id}
                                    nuevoAccionista={nuevaJuridica} permss={permss} soloLectura={soloLectura} />
                            </TabPanel>

                            <TabPanel header="Sección 2" disabled={!juridica?.id} rightIcon="pi pi-chart-line ml-2">
                                <InformacionEconomica toast={toast} id={juridica?.id} back={handlePrev} next={handleNext}
                                    permss={permss} soloLectura={soloLectura} catalogos={catalogos} />
                            </TabPanel>

                            <TabPanel header="Sección 3" disabled={!juridica?.id} rightIcon="pi pi-users ml-2">
                                <AccionistasPep toast={toast} id={juridica?.id} back={handlePrev} next={handleNext}
                                    permss={permss} soloLectura={soloLectura} catalogos={catalogos} />
                            </TabPanel>

                            <TabPanel header="Sección 4" disabled={!juridica?.id} rightIcon="pi pi-file ml-2">
                                <Declaracion toast={toast} id={juridica?.id} setDialogJuridica={setDialogJuridica}
                                    permss={permss} cancelar={!siCliente} catalogos={catalogos} setDialogInstruccion={setDialogInstruccion}
                                    urls={urls} estado={juridica?.nemonico} reiniciar={reiniciar} soloLectura={soloLectura}
                                    mostrarAnexos={() => setDialogSubir(true)} back={handlePrev} />
                            </TabPanel>

                        </TabView>
                    </Panel>

                    {mostrarRegresarFlotante && !siCliente && (
                        <div className="fadein scalein" style={{ position: 'fixed', top: '65px', right: '30px', zIndex: 999 }}>
                            <Button icon="pi pi-arrow-left" onClick={regresar} rounded className="p-button-primary shadow-4" style={{ width: '45px', height: '45px' }} tooltip="Regresar al listado" tooltipOptions={{ position: 'left' }} />
                        </div>
                    )}
                </div>
            }

            <DialogInstrucciones visible={dialogInstruccion} onHide={() => setDialogInstruccion(false)} title={omodel.titulo} items={omodel.pasosInstrucciones || omodel.mensaje} />

            <Dialog visible={dialogSubir} header="Gestión de anexos" modal className="p-fluid modal-ssize" onHide={() => setDialogSubir(false)} style={{ width: '800px' }}>
                <AnexosComponent tipo="juridica" urls={urls} idp={juridica?.id} toast={toast} permss={permss} setDialogSubir={setDialogSubir} reiniciar={reiniciar} estado={juridica?.nemonico} soloLectura={soloLectura} />
            </Dialog>
        </>
    )
}